import { toClassName, type NodeOrFn, renderNodeOrFn, toClassListRecord } from "@mdotm/shadcn/legacy/react-extensions";
import { Text, type InnerRefProps, type StylableProps } from "@mdotm/shadcn/legacy/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { type JSX, type MouseEventHandler, type TouchEventHandler, useMemo } from "react";

export type CardProps = StylableProps &
	InnerRefProps<HTMLDivElement> & {
		title?: NodeOrFn;
		subTitle?: NodeOrFn;
		children: NodeOrFn;
		onMouseDown?: MouseEventHandler<HTMLDivElement>;
		onMouseUp?: MouseEventHandler<HTMLDivElement>;
		onTouchEnd?: TouchEventHandler<HTMLDivElement>;
		footer?: NodeOrFn;
		actionHeader?: NodeOrFn;
	};

// NOTE: this component is used in the PDF pages, in particular the exposure and exposure mixed widgets.
// If you change the height of the title/subtitle, make sure to update the space reserved for this in those print widgets.
export function Card({
	title,
	subTitle,
	children,
	actionHeader,
	style,
	className,
	innerRef,
	...handlers
}: CardProps): JSX.Element {
	const renderedTitle = useMemo(() => {
		if (!title) {
			return undefined;
		}
		const rendered = renderNodeOrFn(title);
		if (typeof rendered === "string") {
			return (
				<Text type="Body/XL/Bold" style={{ marginTop: 0 }} color={themeCSSVars.palette_N800}>
					{rendered}
				</Text>
			);
		}
		return rendered;
	}, [title]);
	return (
		<div
			className={toClassName({ "rounded-sm bg-white px-12 pb-8 flex flex-col": true, ...toClassListRecord(className) })}
			style={style}
			ref={innerRef}
			{...handlers}
		>
			{renderedTitle || actionHeader ? (
				<div className="flex items-center justify-between pb-5 Header">
					{renderedTitle}
					<div>{renderNodeOrFn(actionHeader)}</div>
				</div>
			) : (
				<></>
			)}
			<div className="min-h-0 flex flex-col flex-1">
				{subTitle && <div className="text-[14px] pb-1.5">{renderNodeOrFn(subTitle)}</div>}
				<div className="min-h-0 flex flex-col flex-1">{renderNodeOrFn(children)}</div>
			</div>
		</div>
	);
}
